import React, { useRef, useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  AlertCircle,
  PaintBucket,
  X,
  Undo2,
  Redo2,
  RotateCcw,
} from 'lucide-react';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import SimpleSelect from '@/components/ui/SimpleSelect';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  label: string;
  required?: boolean;
  minHeight?: string;
  originalValue?: string;
  /** Ref that receives a function returning structure-preserved HTML (for save-time). */
  cleanHtmlRef?: React.MutableRefObject<(() => string) | null>;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  error,
  label,
  required = false,
  minHeight = '300px',
  originalValue,
  cleanHtmlRef,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentBgColor, setCurrentBgColor] = useState('#3b82f6');
  const [currentFont, setCurrentFont] = useState('');
  const [currentFontSize, setCurrentFontSize] = useState('');
  const fontJustChangedRef = useRef(false);
  const savedSelectionRef = useRef<Range | null>(null);
  const colorSelectionRef = useRef<Range | null>(null);

  // Guard: when handleInput triggers onChange → value prop changes → useEffect fires,
  // skip re-initialisation (which would re-snapshot cell texts and lose the edit).
  const isInternalChangeRef = useRef(false);

  // Store original background colors for elements (persists across renders)
  const originalColorsMap = useRef<WeakMap<HTMLElement, string>>(new WeakMap());

  // Track the last clicked background element (td, th, a, button)
  const lastClickedElementRef = useRef<HTMLElement | null>(null);

  // Store original cell text content at init time.
  // Used by buildPatchedHtml to skip cells the user didn't modify,
  // preventing browser normalization artifacts (font tags, CSS rewriting, entity decoding).
  const originalCellTexts = useRef<Map<string, string>>(new Map());

  // Store original cell background colors at init time.
  // Used by buildPatchedHtml to detect and persist background color changes.
  const originalCellBgs = useRef<Map<string, string>>(new Map());

  // Custom undo/redo history (browser native undo is unreliable with nested contentEditable)
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const isUndoRedoRef = useRef(false);

  // Convert any CSS color string (hex, rgb, named) to a #rrggbb hex string.
  // Returns null if the color is transparent or unparseable.
  const colorToHex = (raw: string | null | undefined): string | null => {
    if (!raw || raw === 'transparent' || raw === 'rgba(0, 0, 0, 0)') return null;
    if (raw.startsWith('#')) {
      if (raw.length === 4) return '#' + raw[1]+raw[1] + raw[2]+raw[2] + raw[3]+raw[3];
      return raw.slice(0, 7);
    }
    const m = raw.match(/\d+/g);
    if (m && m.length >= 3) {
      return '#' + m.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
    }
    return null;
  };

  // Detect the foreground color of the current selection and update the color picker.
  // Only syncs when the cursor is on actual text — clicking empty space keeps the last color.
  const syncTextColor = () => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !editorRef.current?.contains(sel.anchorNode)) return;
      const anchor = sel.anchorNode;
      if (!anchor || anchor.nodeType !== 3 || !anchor.textContent?.trim()) return;
      const el = anchor.parentElement;
      if (!el) return;
      const hex = colorToHex(window.getComputedStyle(el).color);
      if (hex) setCurrentTextColor(hex);
    } catch { /* ignore */ }
  };

  // Detect the font family and size at the current cursor position and update dropdowns.
  // Skipped briefly after user picks from dropdown so their choice isn't overwritten.
  const syncFontState = () => {
    if (fontJustChangedRef.current) return;
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !editorRef.current?.contains(sel.anchorNode)) return;
      const anchor = sel.anchorNode;
      if (!anchor || anchor.nodeType !== 3 || !anchor.textContent?.trim()) return;
      const font = document.queryCommandValue('fontName').replace(/['"]/g, '');
      if (font) setCurrentFont(font);
      else setCurrentFont('');
      const size = document.queryCommandValue('fontSize');
      if (size) setCurrentFontSize(size);
      else setCurrentFontSize('');
    } catch { /* ignore */ }
  };

  // Save the current cursor position/selection (only if inside editor)
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.startContainer)) {
        savedSelectionRef.current = range.cloneRange();
      }
    }
  };

  // Restore saved selection back into the editor (used by dropdowns that steal focus)
  const restoreSelection = () => {
    if (savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        try {
          sel.removeAllRanges();
          sel.addRange(savedSelectionRef.current);
        } catch {
          // Range nodes may have been removed; just focus the editor
        }
      }
    } else {
      editorRef.current?.focus();
    }
  };

  // Sync the background color picker with the last clicked element's background
  const syncBgColor = () => {
    const el = lastClickedElementRef.current;
    if (!el || !editorRef.current?.contains(el)) return;
    const raw = el.getAttribute('bgcolor') ||
                el.style.backgroundColor ||
                window.getComputedStyle(el).backgroundColor;
    const hex = colorToHex(raw);
    if (hex) setCurrentBgColor(hex);
  };

  // ── Custom undo / redo ─────────────────────────────────────────
  const pushHistory = (html: string) => {
    if (isUndoRedoRef.current) return;
    const history = historyRef.current;
    const idx = historyIndexRef.current;
    if (idx >= 0 && history[idx] === html) return;
    // Discard any redo entries beyond current position
    historyRef.current = history.slice(0, idx + 1);
    historyRef.current.push(html);
    if (historyRef.current.length > 100) historyRef.current.shift();
    historyIndexRef.current = historyRef.current.length - 1;
  };

  const handleUndo = () => {
    if (historyIndexRef.current <= 0 || !editorRef.current) return;
    isUndoRedoRef.current = true;
    historyIndexRef.current--;
    const html = historyRef.current[historyIndexRef.current];
    editorRef.current.innerHTML = html;
    isInternalChangeRef.current = true;
    onChange(unprotectAll(html));
    editorRef.current.focus();
    isUndoRedoRef.current = false;
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1 || !editorRef.current) return;
    isUndoRedoRef.current = true;
    historyIndexRef.current++;
    const html = historyRef.current[historyIndexRef.current];
    editorRef.current.innerHTML = html;
    isInternalChangeRef.current = true;
    onChange(unprotectAll(html));
    editorRef.current.focus();
    isUndoRedoRef.current = false;
  };

  // Track click on background elements (td, th, a, button)
  // Priority: buttons/anchors take precedence over parent cells
  // For td/th clicks, find the OUTERMOST colored cell (header/footer backgrounds)
  const handleEditorClick = (e: React.MouseEvent) => {
    let target = e.target as HTMLElement;

    // First, check if the user directly clicked on a button or anchor element
    // These should take priority over parent table cells
    let directTarget = e.target as HTMLElement;
    while (directTarget && directTarget !== editorRef.current) {
      const tagName = directTarget.tagName?.toLowerCase();
      if (['a', 'button'].includes(tagName)) {
        // User clicked directly on a button/anchor - use it regardless of parent cells
        lastClickedElementRef.current = directTarget;
        syncBgColor();
        return;
      }
      // Stop at first td/th - if we haven't found a button/anchor by now,
      // the user clicked on cell content, not a button
      if (['td', 'th'].includes(tagName)) {
        break;
      }
      directTarget = directTarget.parentElement as HTMLElement;
    }

    // For td/th clicks, find the outermost colored cell (for header/footer backgrounds)
    let outermostColoredElement: HTMLElement | null = null;
    target = e.target as HTMLElement;

    while (target && target !== editorRef.current) {
      const tagName = target.tagName?.toLowerCase();
      if (['td', 'th'].includes(tagName)) {
        // Check if this cell has a background color (not white/transparent)
        const bgColor = target.getAttribute('bgcolor') ||
                        target.style.backgroundColor ||
                        window.getComputedStyle(target).backgroundColor;

        // If it has a non-white background color, track it as the outermost
        if (bgColor &&
            bgColor !== '#ffffff' &&
            bgColor !== 'white' &&
            bgColor !== 'rgb(255, 255, 255)' &&
            bgColor !== 'transparent' &&
            bgColor !== 'rgba(0, 0, 0, 0)') {
          outermostColoredElement = target;
        }
      }
      target = target.parentElement as HTMLElement;
    }

    // If we found a colored cell, use it; otherwise use the first td/th found
    if (outermostColoredElement) {
      lastClickedElementRef.current = outermostColoredElement;
      syncBgColor();
    } else {
      // Fallback: find the first td/th
      target = e.target as HTMLElement;
      while (target && target !== editorRef.current) {
        const tagName = target.tagName?.toLowerCase();
        if (['td', 'th'].includes(tagName)) {
          lastClickedElementRef.current = target;
          syncBgColor();
          return;
        }
        target = target.parentElement as HTMLElement;
      }
    }
  };

  // Check if a DOM node is inside an editable area
  const isInsideEditable = (node: Node | null): boolean => {
    let current = node;
    while (current && current !== editorRef.current) {
      if (current.nodeType === 1) {
        const el = current as HTMLElement;
        const ce = el.getAttribute('contenteditable');
        if (ce === 'false') return false;
        if (ce === 'true') return true;
      }
      current = current.parentNode;
    }
    return true; // inside the editor root which is contentEditable
  };

  // Drop handler for variables
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'VARIABLE',
    drop: (item: { name: string; label: string }, monitor) => {
      const variable = `{{${item.name}}}`;
      const offset = monitor.getClientOffset();
      if (offset && editorRef.current) {
        // Place caret at the exact drop coordinates
        const range = (document as any).caretRangeFromPoint?.(offset.x, offset.y)
          ?? (document as any).caretPositionFromPoint?.(offset.x, offset.y);
        if (range) {
          const targetNode = range instanceof Range
            ? range.startContainer
            : range.offsetNode;

          // Insert at the exact caret position if it's inside an editable area
          if (targetNode && isInsideEditable(targetNode)) {
            editorRef.current.focus();
            const sel = window.getSelection();
            if (sel) {
              if (range instanceof Range) {
                sel.removeAllRanges();
                sel.addRange(range);
              } else {
                const r = document.createRange();
                r.setStart(range.offsetNode, range.offset);
                r.collapse(true);
                sel.removeAllRanges();
                sel.addRange(r);
              }
            }
            document.execCommand('insertHTML', false, variable);
            handleInput();
            return;
          }
        }
      }
      // Fallback: insert at last known cursor position (saved on blur/mouseUp)
      restoreSelection();
      document.execCommand('insertHTML', false, variable);
      handleInput();
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  // ── Document structure helpers ──────────────────────────────────
  // Email templates are full HTML documents (DOCTYPE, <html>, <head>, <body>).
  // Browsers strip these when set as innerHTML of a <div>.
  // We extract only the <body> content for the editor and reconstruct on save.
  const extractBodyContent = (html: string): string => {
    const m = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return m ? m[1] : html;
  };

  // ── Variable protection ───────────────────────────────────────
  // {{variables}} sitting between table rows (e.g. {{notes_section}})
  // are TEXT nodes inside <table>/<tbody>, which the browser's HTML
  // parser "foster-parents" (moves before the table), corrupting the
  // template. Encode ALL {{...}} between closing/opening table tags
  // as HTML comments that the parser preserves in-place.
  const protectHandlebars = (html: string): string =>
    html.replace(/\{\{([#\/][^}]*|else)\}\}/g, (match) => {
      try { return `<!--HBS_BLOCK:${btoa(match)}-->`; } catch { return match; }
    });

  const unprotectHandlebars = (html: string): string =>
    html.replace(/<!--HBS_BLOCK:([A-Za-z0-9+/=]+)-->/g, (_, encoded) => {
      try { return atob(encoded); } catch { return ''; }
    });

  // Protect row-level variables: {{var}} that sits between </tr> and <tr>
  // (or adjacent to other table structural tags). These are typically
  // section injectors like {{notes_section}}, {{mandatory_notice_section}}.
  const protectRowVars = (html: string): string =>
    html.replace(
      /(<\/(?:tr|thead|tbody|tfoot)\s*>)(\s*\{\{[^}]+\}\}\s*)(?=<(?:tr|td|th|thead|tbody|tfoot|\/table)\b)/gi,
      (_, closeTag, variable) => {
        try {
          return `${closeTag}<!--HBS_ROW:${btoa(variable.trim())}-->`;
        } catch { return _; }
      }
    );

  const unprotectRowVars = (html: string): string =>
    html.replace(/<!--HBS_ROW:([A-Za-z0-9+/=]+)-->/g, (_, encoded) => {
      try { return atob(encoded); } catch { return ''; }
    });

  // ── Edit IDs ──────────────────────────────────────────────────
  const assignEditIds = (html: string): string => {
    let counter = 0;
    return html.replace(/<(td|th)\b([^>]*?)>/gi, (match, tag, attrs) => {
      if (/data-edit-id/i.test(attrs)) return match;
      return `<${tag}${attrs} data-edit-id="eid-${counter++}">`;
    });
  };

  const removeEditIds = (html: string): string =>
    html.replace(/\s*data-edit-id="[^"]*"/gi, '');

  // ── Table protection ──────────────────────────────────────────
  const protectTables = (html: string): string => {
    let result = html;
    ['table', 'thead', 'tbody', 'tfoot', 'tr'].forEach(tag => {
      const re = new RegExp(`<${tag}\\b([^>]*?)>`, 'gi');
      result = result.replace(re, (m, attrs) =>
        /contenteditable/i.test(attrs) ? m : `<${tag}${attrs} contenteditable="false">`
      );
    });
    ['td', 'th'].forEach(tag => {
      const re = new RegExp(`<${tag}\\b([^>]*?)>`, 'gi');
      result = result.replace(re, (m, attrs) =>
        /contenteditable/i.test(attrs) ? m : `<${tag}${attrs} contenteditable="true">`
      );
    });
    return result;
  };

  const unprotectTables = (html: string): string =>
    html.replace(/\s*contenteditable="(?:true|false)"/gi, '');

  // Combined pipelines:
  // protectForEditor: extract body, protect vars + tables → safe for innerHTML
  const protectForEditor = (html: string): string =>
    protectTables(assignEditIds(protectRowVars(protectHandlebars(extractBodyContent(html)))));

  // unprotectFromEditor: strip editor attrs, restore vars → clean body HTML
  const unprotectAll = (html: string): string =>
    removeEditIds(unprotectRowVars(unprotectHandlebars(unprotectTables(html))));

  // ── Nesting-aware cell content replacement ────────────────────
  // Finds a cell by data-edit-id in the HTML string, then replaces
  // everything between its opening and closing tags.
  const replaceCellContent = (
    html: string, editId: string, newContent: string
  ): string => {
    const escapedId = editId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const openRe = new RegExp(
      `<(td|th)\\b[^>]*?data-edit-id="${escapedId}"[^>]*?>`, 'i'
    );
    const m = openRe.exec(html);
    if (!m) return html;

    const tagName = m[1].toLowerCase();
    const contentStart = m.index + m[0].length;

    // Walk forward tracking nesting depth to find the matching </td|th>
    let depth = 1;
    let pos = contentStart;
    while (depth > 0 && pos < html.length) {
      const oi = html.toLowerCase().indexOf(`<${tagName}`, pos);
      const ci = html.toLowerCase().indexOf(`</${tagName}>`, pos);
      if (ci === -1) break; // malformed
      if (oi !== -1 && oi < ci) {
        depth++;
        pos = oi + tagName.length + 1;
      } else {
        depth--;
        if (depth === 0) {
          return html.substring(0, contentStart) + newContent + html.substring(ci);
        }
        pos = ci + tagName.length + 3;
      }
    }
    return html; // fallback — couldn't find close tag
  };

  // Update a cell's opening tag to set/replace background-color in its style attribute
  const patchCellBgColor = (html: string, editId: string, bgColor: string): string => {
    const escapedId = editId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const openRe = new RegExp(
      `(<(?:td|th)\\b)([^>]*?data-edit-id="${escapedId}"[^>]*?)>`, 'i'
    );
    const m = openRe.exec(html);
    if (!m) return html;

    let tag = m[0];
    // Update or add style attribute with background-color
    if (/style\s*=\s*"/i.test(tag)) {
      // Has existing style — replace or append background-color
      tag = tag.replace(/(style\s*=\s*")([^"]*)"/, (_m, prefix, styles) => {
        const cleaned = styles.replace(/background-color\s*:[^;]*(;|$)/gi, '').replace(/;\s*$/, '');
        const newStyles = cleaned ? `${cleaned};background-color:${bgColor}` : `background-color:${bgColor}`;
        return `${prefix}${newStyles}"`;
      });
    } else {
      // No style attribute — add one before the closing >
      tag = tag.replace(/>$/, ` style="background-color:${bgColor}">`);
    }
    // Also set bgcolor attribute for email client compatibility
    if (/bgcolor\s*=\s*"/i.test(tag)) {
      tag = tag.replace(/bgcolor\s*=\s*"[^"]*"/i, `bgcolor="${bgColor}"`);
    } else {
      tag = tag.replace(/>$/, ` bgcolor="${bgColor}">`);
    }
    return html.substring(0, m.index) + tag + html.substring(m.index + m[0].length);
  };

  // ── Build patched HTML ────────────────────────────────────────
  // Instead of trusting innerHTML (which the browser corrupts), take
  // the ORIGINAL DB template (via originalValue prop) and surgically
  // replace only each cell's content with what the user edited in the DOM.
  // This preserves DOCTYPE, <html>, <head>, <body> and row-level variables.
  const buildPatchedHtml = (): string => {
    const editor = editorRef.current;
    if (!editor) return '';

    // Use the originalValue prop directly — it always has the full document
    // structure from the DB (DOCTYPE, html, head, body).
    const base = originalValue || '';
    if (!base) {
      return unprotectAll(editor.innerHTML);
    }

    // Add edit IDs fresh (counter-based, same order as the editor DOM)
    let patched = assignEditIds(base);

    // Only patch LEAF cells (no nested data-edit-id children).
    // Non-leaf cells contain table structure that must be preserved from the
    // original HTML. Additionally, only patch cells the user actually CHANGED —
    // skip cells where textContent is identical to the initial snapshot.
    // This prevents browser normalization artifacts (<font> tags, CSS rewriting,
    // <tbody> insertion, entity decoding) from corrupting cells the user never touched.
    const cells = editor.querySelectorAll('[data-edit-id]');
    cells.forEach(cell => {
      // Skip non-leaf cells — they contain nested tables
      if (cell.querySelector('[data-edit-id]')) return;

      const editId = cell.getAttribute('data-edit-id');
      if (!editId) return;

      // Skip cells the user didn't modify (textContent unchanged from init snapshot)
      const currentText = (cell.textContent || '').trim();
      const originalText = originalCellTexts.current.get(editId);
      if (originalText !== undefined && currentText === originalText) return;

      // Clean the cell's innerHTML: strip editor attrs, restore all protected vars
      let content = cell.innerHTML;
      content = content.replace(/\s*contenteditable="(?:true|false)"/gi, '');
      content = unprotectHandlebars(content);
      content = unprotectRowVars(content);
      content = removeEditIds(content);

      patched = replaceCellContent(patched, editId, content);
    });

    // Patch background color changes on ALL cells (leaf and non-leaf).
    // Background color is on the <td> opening tag's style attribute, not the innerHTML,
    // so it needs separate handling from content patching.
    cells.forEach(cell => {
      const editId = (cell as HTMLElement).getAttribute('data-edit-id');
      if (!editId) return;
      const el = cell as HTMLElement;
      const currentBg = el.style.backgroundColor || '';
      const originalBg = originalCellBgs.current.get(editId) || '';
      if (currentBg && currentBg !== originalBg) {
        patched = patchCellBgColor(patched, editId, currentBg);
      }
    });

    // Strip edit IDs from the final output
    return removeEditIds(patched);
  };

  // Expose buildPatchedHtml to parent for save-time use.
  // Must update on every render so the closure captures the latest originalValue
  // (originalValue is undefined on first render, loaded asynchronously via useQuery).
  useEffect(() => {
    if (cleanHtmlRef) cleanHtmlRef.current = buildPatchedHtml;
    return () => { if (cleanHtmlRef) cleanHtmlRef.current = null; };
  });

  // ── Initialise / sync editor ──────────────────────────────────
  useEffect(() => {
    // When the value change came from handleInput (user typing / drag-drop),
    // the DOM already has the correct content — skip re-init so we don't
    // re-snapshot cell texts (which would make buildPatchedHtml think
    // nothing changed and lose the edit on save).
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    if (editorRef.current && unprotectAll(editorRef.current.innerHTML) !== extractBodyContent(value)) {
      editorRef.current.innerHTML = protectForEditor(value);

      // Snapshot each leaf cell's textContent so buildPatchedHtml can detect dirty cells.
      // Also snapshot ALL cells' backgroundColor for background color change detection.
      originalCellTexts.current.clear();
      originalCellBgs.current.clear();
      editorRef.current.querySelectorAll('[data-edit-id]').forEach(cell => {
        const eid = cell.getAttribute('data-edit-id');
        if (!eid) return;
        originalCellBgs.current.set(eid, (cell as HTMLElement).style.backgroundColor || '');
        if (cell.querySelector('[data-edit-id]')) return; // skip non-leaf for text
        originalCellTexts.current.set(eid, (cell.textContent || '').trim());
      });

      // Initialize undo/redo history with this content
      historyRef.current = [editorRef.current.innerHTML];
      historyIndexRef.current = 0;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChangeRef.current = true;
      pushHistory(editorRef.current.innerHTML);
      onChange(unprotectAll(editorRef.current.innerHTML));
    }
  };

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Recover: reset editor to the original DB-saved template
  const handleRecover = () => {
    if (originalValue == null) return;
    setShowRecoverModal(true);
  };

  const confirmRecover = () => {
    if (originalValue != null && editorRef.current) {
      editorRef.current.innerHTML = protectForEditor(originalValue);
      onChange(extractBodyContent(originalValue));
      // Re-snapshot cell texts and backgrounds after recovery
      originalCellTexts.current.clear();
      originalCellBgs.current.clear();
      editorRef.current.querySelectorAll('[data-edit-id]').forEach(cell => {
        const eid = cell.getAttribute('data-edit-id');
        if (!eid) return;
        originalCellBgs.current.set(eid, (cell as HTMLElement).style.backgroundColor || '');
        if (cell.querySelector('[data-edit-id]')) return;
        originalCellTexts.current.set(eid, (cell.textContent || '').trim());
      });

      // Reset undo/redo history after recovery
      historyRef.current = [editorRef.current.innerHTML];
      historyIndexRef.current = 0;
    }
    setShowRecoverModal(false);
  };


  const borderClass = isOver && canDrop
    ? 'border-brand-blue-500 ring-2 ring-brand-blue-200'
    : error
    ? 'border-red-500'
    : isFocused
    ? 'border-primary-500 ring-2 ring-primary-200'
    : 'border-gray-300';

  const bgClass = isOver && canDrop ? 'bg-brand-blue-50' : 'bg-white';

  const ToolbarButton = ({
    onClick,
    icon: Icon,
    title,
  }: {
    onClick: () => void;
    icon: any;
    title: string;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-2 hover:bg-gray-100 rounded transition-colors"
      title={title}
    >
      <Icon className="w-4 h-4 text-gray-700" />
    </button>
  );

  // Apply background color to element (header, button, etc.)
  const applyElementBackground = (color: string): boolean => {
    // Use the last clicked element directly
    const element = lastClickedElementRef.current;

    if (!element || !editorRef.current?.contains(element)) {
      return false; // No valid element selected
    }

    const tagName = element.tagName.toLowerCase();
    if (['td', 'th', 'a', 'button'].includes(tagName)) {
      // Save the original color before applying new one (only if not already saved)
      if (!originalColorsMap.current.has(element)) {
        const currentBg = element.getAttribute('bgcolor') ||
                          element.style.backgroundColor ||
                          window.getComputedStyle(element).backgroundColor ||
                          '#ffffff';
        originalColorsMap.current.set(element, currentBg);
      }

      element.style.backgroundColor = color;
      // Also set bgcolor attribute for better email client compatibility
      if (['td', 'th'].includes(tagName)) {
        element.setAttribute('bgcolor', color);
      }
      handleInput();
      return true;
    }

    return false;
  };

  // Remove/Reset background color from element - restores to original color
  const removeElementBackground = (): boolean => {
    // Use the last clicked element directly
    const element = lastClickedElementRef.current;

    if (!element || !editorRef.current?.contains(element)) {
      return false; // No valid element selected
    }

    const tagName = element.tagName.toLowerCase();
    if (['td', 'th', 'a', 'button'].includes(tagName)) {
      // Get the original color from WeakMap
      const originalColor = originalColorsMap.current.get(element) || '#ffffff';

      // Restore background color to original
      element.style.backgroundColor = originalColor;
      // Also set bgcolor attribute for email client compatibility
      if (['td', 'th'].includes(tagName)) {
        element.setAttribute('bgcolor', originalColor);
      }

      // Remove from map so next color change saves new original
      originalColorsMap.current.delete(element);

      handleInput();
      return true;
    }

    return false;
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Toolbar */}
      <div ref={toolbarRef} className="border border-b-0 rounded-t-lg bg-gray-50 p-2 flex flex-wrap items-center gap-1">
        {/* Font & size selects inlined (not wrapper components) to prevent
            React remount on re-render which kills the dropdown on first click */}
        <div className="w-40 flex-shrink-0">
          <SimpleSelect
            compact
            isSearchable={false}
            placeholder="Font"
            value={currentFont}
            onChange={(value) => { if (!value) return; const v = String(value); setCurrentFont(v); fontJustChangedRef.current = true; restoreSelection(); execCommand('fontName', v); setTimeout(() => { fontJustChangedRef.current = false; }, 500); }}
            options={[
              { value: 'Arial', label: 'Arial' },
              { value: 'Helvetica', label: 'Helvetica' },
              { value: 'Times New Roman', label: 'Times New Roman' },
              { value: 'Georgia', label: 'Georgia' },
              { value: 'Courier New', label: 'Courier New' },
              { value: 'Verdana', label: 'Verdana' },
            ]}
          />
        </div>
        <div className="w-28 flex-shrink-0">
          <SimpleSelect
            compact
            isSearchable={false}
            placeholder="Size"
            value={currentFontSize}
            onChange={(value) => { if (!value) return; const v = String(value); setCurrentFontSize(v); fontJustChangedRef.current = true; restoreSelection(); execCommand('fontSize', v); setTimeout(() => { fontJustChangedRef.current = false; }, 500); }}
            options={[
              { value: '1', label: 'Small' },
              { value: '3', label: 'Normal' },
              { value: '5', label: 'Large' },
              { value: '7', label: 'Huge' },
            ]}
          />
        </div>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={() => execCommand('bold')} icon={Bold} title="Bold" />
        <ToolbarButton onClick={() => execCommand('italic')} icon={Italic} title="Italic" />
        <ToolbarButton
          onClick={() => execCommand('underline')}
          icon={Underline}
          title="Underline"
        />
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton
          onClick={() => execCommand('justifyLeft')}
          icon={AlignLeft}
          title="Align Left"
        />
        <ToolbarButton
          onClick={() => execCommand('justifyCenter')}
          icon={AlignCenter}
          title="Align Center"
        />
        <ToolbarButton
          onClick={() => execCommand('justifyRight')}
          icon={AlignRight}
          title="Align Right"
        />
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton
          onClick={() => execCommand('insertUnorderedList')}
          icon={List}
          title="Bullet List"
        />
        <ToolbarButton
          onClick={() => execCommand('insertOrderedList')}
          icon={ListOrdered}
          title="Numbered List"
        />
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {/* Text Color — syncs with selected text color via currentTextColor state */}
        <input
          type="color"
          value={currentTextColor}
          onMouseDown={() => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
              colorSelectionRef.current = sel.getRangeAt(0).cloneRange();
            }
          }}
          onChange={(e) => {
            const sel = window.getSelection();
            if (colorSelectionRef.current && sel) {
              sel.removeAllRanges();
              sel.addRange(colorSelectionRef.current);
            }
            document.execCommand('foreColor', false, e.target.value);
            setCurrentTextColor(e.target.value);
            handleInput();
          }}
          className="w-8 h-8 border rounded cursor-pointer"
          title="Text Color"
        />
        {/* Element Background - for header/button backgrounds */}
        <div className="flex items-center gap-0.5">
          <label
            className="flex items-center gap-1 p-2 hover:bg-gray-100 rounded cursor-pointer"
            title="Element/Cell Background (click inside header or button first)"
          >
            <PaintBucket className="w-4 h-4 text-gray-700" />
            <input
              type="color"
              value={currentBgColor}
              onChange={(e) => { applyElementBackground(e.target.value); setCurrentBgColor(e.target.value); }}
              className="w-5 h-5 border rounded cursor-pointer"
            />
          </label>
          {/* Remove/Reset Background Color Button */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={removeElementBackground}
            className="p-1.5 hover:bg-red-100 rounded transition-colors group"
            title="Remove Background Color (click inside element first)"
          >
            <X className="w-4 h-4 text-gray-500 group-hover:text-red-600" />
          </button>
        </div>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={handleUndo} icon={Undo2} title="Undo" />
        <ToolbarButton onClick={handleRedo} icon={Redo2} title="Redo" />
        {originalValue != null && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleRecover}
              className="flex items-center gap-1 px-2 py-1 text-xs text-orange-700 hover:bg-orange-50 rounded transition-colors"
              title="Recover original template from database"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Recover
            </button>
          </>
        )}
      </div>

      {/* Editor */}
      <div ref={drop} className="overflow-hidden rounded-b-lg">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onFocus={() => { setIsFocused(true); syncTextColor(); syncFontState(); }}
          onBlur={(e) => {
            // Skip unfocus when focus moves to toolbar (Font/Size selects, color pickers).
            // Re-rendering during blur kills the dropdown on first click.
            if (toolbarRef.current && e.relatedTarget && toolbarRef.current.contains(e.relatedTarget as Node)) {
              saveSelection();
              return;
            }
            setIsFocused(false);
            saveSelection();
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
              e.preventDefault();
              if (e.shiftKey) handleRedo(); else handleUndo();
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
              e.preventDefault();
              handleRedo();
            }
          }}
          onClick={(e) => { handleEditorClick(e); syncTextColor(); syncFontState(); }}
          onMouseUp={() => { saveSelection(); syncTextColor(); syncFontState(); }}
          onKeyUp={() => { saveSelection(); syncTextColor(); syncFontState(); }}
          className={`w-full px-4 py-3 border rounded-b-lg focus:outline-none transition-all overflow-x-auto ${borderClass} ${bgClass}`}
          style={{ minHeight, maxWidth: '100%', wordWrap: 'break-word', overflowWrap: 'break-word' }}
          data-placeholder={placeholder}
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}

      {isOver && canDrop && (
        <p className="text-xs text-brand-blue-600 mt-1">Drop to insert variable</p>
      )}

      {!error && !isOver && (
        <p className="text-xs text-gray-500 mt-1">
          Use toolbar to format text. For backgrounds: click inside element, use paint bucket to apply color or X to remove.
        </p>
      )}

      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] > * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        [contenteditable] ol {
          list-style-type: decimal !important;
          padding-left: 24px !important;
          list-style-position: inside !important;
        }
        [contenteditable] ul {
          list-style-type: disc !important;
          padding-left: 24px !important;
          list-style-position: inside !important;
        }
        [contenteditable] li {
          display: list-item !important;
          margin-bottom: 6px;
          list-style-position: inside !important;
        }
        [contenteditable] table {
          max-width: 100% !important;
        }
        [contenteditable] td,
        [contenteditable] th {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        [contenteditable] img {
          max-width: 100% !important;
          height: auto !important;
        }
        [contenteditable] div,
        [contenteditable] section,
        [contenteditable] article {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
      `}</style>

      <ConfirmationModal
        isOpen={showRecoverModal}
        title="Recover Original Template"
        message="Reset content to the original saved template? Your current changes will be lost."
        type="warning"
        confirmText="Recover"
        cancelText="Cancel"
        onConfirm={confirmRecover}
        onCancel={() => setShowRecoverModal(false)}
      />
    </div>
  );
}
